import React from "react";
import { Trophy } from "lucide-react";

import { cn } from "@/lib/utils";
import type { Deployment } from "../../../types/strategyDeploy";
import { BROKER_LABEL, SURF, fmtMoney, fmtPct, fmtR, signColor } from "./liveStrategiesFormat";
import { useAccountLabel } from "./useAccountLabel";
import { DeploymentStatusDot } from "./DeploymentCard";
import {
  liveCardBodyClassName,
  liveCardClassName,
  liveFigureClassName,
  liveLabelClassName,
  liveOptionTitleClassName,
  liveSmallTextClassName,
} from "./liveStrategiesUi";
import { DeployModeBadge } from "./LiveUiParts";

/** Deployments of one strategy family by family id (every Lab version shares its root). */
export function familyOf(d: Deployment): string {
  const bt = d.strategy_snapshot?.backtest as { root_id?: string } | undefined;
  return bt?.root_id || d.hypothesis_id || `own:${d.id}`;
}

function versionOf(d: Deployment): string {
  const v = (d.strategy_snapshot?.backtest as { version?: number } | undefined)?.version;
  return v ? `v${v}` : "";
}

/** One strategy running several ways at once (paper + live, or several versions): the same
 *  numbers side by side, best result marked. */
export function ComparePanel({ deps, onOpen }: { deps: Deployment[]; onOpen: (id: string) => void }): JSX.Element {
  const accountName = useAccountLabel();
  const rows = deps.map((d) => {
    const s = d.summary ?? {};
    const closed = s.closed ?? 0;
    return {
      d,
      closed,
      won: closed ? (s.wins ?? 0) / closed : null,
      avgR: closed ? (s.r_total ?? 0) / closed : null,
      r: s.r_total ?? 0,
      pnl: s.pnl ?? 0,
      open: s.open_trades ?? 0,
    };
  });
  const best = rows.some((r) => r.closed) ? rows.reduce((a, b) => (b.r > a.r ? b : a)) : null;
  const title = deps[0]?.strategy_snapshot?.backtest?.name || deps[0]?.name;
  return (
    <div className={cn(liveCardClassName, liveCardBodyClassName)} data-live-compare="">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className={cn(liveOptionTitleClassName, "text-base")}>{title}</span>
        <span className={liveSmallTextClassName}>
          Running {deps.length} ways side by side
        </span>
      </div>
      {/* Small screens: one block per run. */}
      <div className="flex flex-col sm:hidden [&>*+*]:!border-0 [&>*+*]:!border-t [&>*+*]:!border-solid [&>*+*]:!border-border">
        {rows.map((r) => {
          return (
            <button
              key={r.d.id}
              type="button"
              onClick={() => onOpen(r.d.id)}
              className="flex flex-col gap-2 bg-transparent py-3 text-left"
            >
              <span className="flex items-center gap-2">
                <DeploymentStatusDot status={r.d.status} />
                <span className="min-w-0 flex-1 truncate font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">{r.d.name}</span>
                {best && best.d.id === r.d.id ? <Trophy className="size-3.5" style={{ color: SURF }} /> : null}
                <DeployModeBadge mode={r.d.mode} />
              </span>
              <span className="grid grid-cols-4 gap-2">
                {[
                  ["Trades", String(r.closed), undefined],
                  ["Won", r.won != null ? fmtPct(r.won) : "N/A", undefined],
                  ["Total R", fmtR(r.r), signColor(r.r)],
                  ["P&L", fmtMoney(r.pnl), signColor(r.pnl)],
                ].map(([k, v, c]) => (
                  <span key={k} className="flex min-w-0 flex-col">
                    <span className={liveLabelClassName}>{k}</span>
                    <span className={cn(liveFigureClassName, "truncate")} style={c ? { color: c } : undefined}>{v}</span>
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>

      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[620px] text-sm">
          <thead>
            <tr className="text-left">
              {["Run", "Mode", "Account", "Trades", "Won", "Avg R", "Total R", "P&L"].map((h) => (
                <th key={h} className={cn(liveLabelClassName, "px-3 pb-3")}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
                  const isBest = best && best.d.id === r.d.id;
              return (
                <tr
                  key={r.d.id}
                  className="cursor-pointer !border-0 !border-t !border-solid !border-border text-gray-500 transition-colors hover:bg-muted/40 dark:text-white"
                  onClick={() => onOpen(r.d.id)}
                >
                  <td className="px-3 py-3">
                    <span className="flex items-center gap-2">
                      <DeploymentStatusDot status={r.d.status} />
                      <span className="truncate font-[family-name:var(--font-primary)] font-medium text-[color:var(--brand-tropical-surf)]">{r.d.name}</span>
                      {versionOf(r.d) ? <span className={liveSmallTextClassName}>{versionOf(r.d)}</span> : null}
                      {isBest ? <Trophy className="size-3.5" style={{ color: SURF }} aria-label="Best so far" /> : null}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <DeployModeBadge mode={r.d.mode} />
                  </td>
                  <td className={cn(liveSmallTextClassName, "px-3 py-3")}>
                    {r.d.broker_name ? `${BROKER_LABEL[r.d.broker_name] ?? r.d.broker_name} ${accountName(r.d.broker_name, r.d.broker_account_code)}` : "Paper"}
                  </td>
                  <td className="px-3 py-3 font-ibm-plex font-medium tabular-nums">
                    {r.closed}
                    {r.open ? <span className="text-xs" style={{ color: SURF }}> +{r.open}</span> : null}
                  </td>
                  <td className="px-3 py-3 font-ibm-plex font-medium tabular-nums">{r.won != null ? fmtPct(r.won) : "N/A"}</td>
                  <td className="px-3 py-3 font-ibm-plex font-medium tabular-nums" style={{ color: signColor(r.avgR) }}>{r.avgR != null ? fmtR(r.avgR) : "N/A"}</td>
                  <td className="px-3 py-3 font-ibm-plex font-medium tabular-nums" style={{ color: signColor(r.r) }}>{fmtR(r.r)}</td>
                  <td className="px-3 py-3 font-ibm-plex font-medium tabular-nums" style={{ color: signColor(r.pnl) }}>{fmtMoney(r.pnl)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <span className={liveSmallTextClassName}>
        Paper and live copies of one version take the same signals. Differences come from fills, sizing and what you approve. Versions differ in their rules.
      </span>
    </div>
  );
}
